import React, { useState } from 'react';
import { Card, Typography, Collapse, Input, Space, Tag, Button, Tooltip, Badge } from 'antd';
import { SearchOutlined, QuestionCircleOutlined, BookOutlined, VideoCameraOutlined, FileTextOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Panel } = Collapse;

interface FAQItem {
  key: string;
  question: string;
  answer: string;
  category: string;
  tags: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  hasVideo?: boolean;
  hasGuide?: boolean;
}

const faqData: FAQItem[] = [
  // Getting Started
  {
    key: 'getting-started-1',
    question: 'How do I get started with the PA CRM system?',
    answer: 'Getting started is easy! First, complete your profile setup by adding your contact information and preferences. Then, explore the dashboard to understand your key metrics. We recommend starting with creating your first claim to familiarize yourself with the workflow. Use the "Quick Start Guide" in the Help section for step-by-step instructions.',
    category: 'Getting Started',
    tags: ['onboarding', 'setup', 'first-time'],
    difficulty: 'beginner',
    hasGuide: true,
  },
  {
    key: 'getting-started-2',
    question: 'What are the system requirements for optimal performance?',
    answer: 'For optimal performance, we recommend: Chrome/Firefox/Safari/Edge (latest versions), minimum 4GB RAM, stable internet connection (5+ Mbps), and at least 2GB free storage. Mobile users need iOS 12+ or Android 8+. Enable JavaScript, cookies, and allow pop-ups for the application.',
    category: 'Getting Started',
    tags: ['system-requirements', 'performance', 'browser'],
    difficulty: 'beginner',
  },
  {
    key: 'getting-started-3',
    question: 'How do I set up my user profile and preferences?',
    answer: 'Navigate to Settings → Profile to configure your account. Add your photo, contact information, and notification preferences. Set up your timezone, date format, and language preferences. Configure email templates and signature for professional communications.',
    category: 'Getting Started',
    tags: ['profile', 'settings', 'preferences'],
    difficulty: 'beginner',
  },

  // Claims Management
  {
    key: 'claims-1',
    question: 'How do I create and manage a new claim?',
    answer: 'Click "New Claim" from the Claims section or dashboard. Fill in the required information: claim number, client details, insurance company, policy number, date of loss, and property details. Upload initial documentation and photos. Use the status dropdown to track progress through the claim lifecycle.',
    category: 'Claims Management',
    tags: ['claims', 'create', 'workflow'],
    difficulty: 'beginner',
    hasVideo: true,
    hasGuide: true,
  },
  {
    key: 'claims-2',
    question: 'How do I track claim status and progress?',
    answer: 'Each claim has a status indicator and timeline. Use the Claims dashboard to view all claims with their current status. Click on any claim to see detailed progress, communications history, and next steps. Set up notifications to be alerted when status changes occur.',
    category: 'Claims Management',
    tags: ['claims', 'status', 'tracking', 'progress'],
    difficulty: 'beginner',
  },
  {
    key: 'claims-3',
    question: 'How do I manage damage assessments and estimates?',
    answer: 'Navigate to the "Damage Assessment" tab within a claim. Upload photos and videos of damage, enter detailed descriptions of affected areas, and add repair estimates. Use the built-in calculator for cost assessments. Organize documentation by room or area for better tracking.',
    category: 'Claims Management',
    tags: ['damage-assessment', 'estimates', 'photos', 'documentation'],
    difficulty: 'intermediate',
    hasVideo: true,
  },
  {
    key: 'claims-4',
    question: 'How do I handle insurance company communications?',
    answer: 'Use the "Communications" tab to log all interactions with insurance companies. Record phone calls, emails, and letters with timestamps and outcomes. Set follow-up reminders and track response times. Use templates for consistent communication and maintain a complete audit trail.',
    category: 'Claims Management',
    tags: ['communications', 'insurance', 'audit-trail'],
    difficulty: 'intermediate',
  },

  // Client Management
  {
    key: 'clients-1',
    question: 'How do I manage client information and communications?',
    answer: 'Create comprehensive client profiles with contact information, property details, and insurance information. Track all communications and interactions in the client timeline. Set up automated notifications for status updates and use the client portal for secure document sharing.',
    category: 'Client Management',
    tags: ['clients', 'profiles', 'communications'],
    difficulty: 'beginner',
  },
  {
    key: 'clients-2',
    question: 'How do I organize contacts and create groups?',
    answer: 'Use the Contacts section to add and organize all your contacts. Create custom groups for different types of contacts (clients, adjusters, contractors, etc.). Add custom fields for specific information and use tags for easy filtering and searching.',
    category: 'Client Management',
    tags: ['contacts', 'groups', 'organization'],
    difficulty: 'intermediate',
  },

  // Document Management
  {
    key: 'documents-1',
    question: 'How do I upload and organize documents?',
    answer: 'Use the document upload feature to add files to claims or client profiles. Supported formats include PDF, DOC, XLS, JPG, PNG, and more. Organize documents by category (photos, estimates, contracts, etc.) and use tags for easy searching. Enable automatic backup for important documents.',
    category: 'Document Management',
    tags: ['documents', 'upload', 'organization'],
    difficulty: 'beginner',
    hasVideo: true,
  },
  {
    key: 'documents-2',
    question: 'How do I generate and customize reports?',
    answer: 'Navigate to the Reports section and choose from various report types. Select date ranges, filters, and customize the output format. Export reports as PDF, Excel, or CSV. Schedule automated reports for regular updates and share them with clients or team members.',
    category: 'Document Management',
    tags: ['reports', 'analytics', 'export'],
    difficulty: 'intermediate',
    hasGuide: true,
  },

  // Financial Management
  {
    key: 'financial-1',
    question: 'How do I track settlements and payments?',
    answer: 'Use the Financial section to record settlement amounts, payment schedules, and commission calculations. Track payment status and set up reminders for follow-ups. Generate financial reports for tax purposes and business analysis. Integrate with accounting software for seamless record-keeping.',
    category: 'Financial Management',
    tags: ['settlements', 'payments', 'commission'],
    difficulty: 'intermediate',
  },
  {
    key: 'financial-2',
    question: 'How do I calculate fees and commissions?',
    answer: 'The system automatically calculates fees based on your configured rates and settlement amounts. Set up different fee structures for different types of claims. Track commission splits with team members and generate detailed financial reports for business analysis.',
    category: 'Financial Management',
    tags: ['fees', 'commission', 'calculations'],
    difficulty: 'intermediate',
  },

  // Integrations & Tools
  {
    key: 'integrations-1',
    question: 'How do I integrate with email and calendar systems?',
    answer: 'Configure email integration in Settings → Integrations. Connect your email account for seamless communication tracking. Set up calendar integration to sync appointments and deadlines. Use email templates for consistent professional communications.',
    category: 'Integrations & Tools',
    tags: ['email', 'calendar', 'integration'],
    difficulty: 'intermediate',
    hasGuide: true,
  },
  {
    key: 'integrations-2',
    question: 'How do I use the mobile app?',
    answer: 'Download the PA CRM mobile app from your device\'s app store. Log in with your existing credentials. Use the app for on-site damage assessments, photo capture, and quick claim updates. Sync automatically with the web application for seamless workflow.',
    category: 'Integrations & Tools',
    tags: ['mobile', 'app', 'field-work'],
    difficulty: 'beginner',
    hasVideo: true,
  },

  // Security & Privacy
  {
    key: 'security-1',
    question: 'How is my data protected and secured?',
    answer: 'We use enterprise-grade security measures including SSL encryption, regular security audits, and HIPAA compliance for sensitive information. Data is backed up daily with multiple redundant systems. Access is controlled through role-based permissions and two-factor authentication.',
    category: 'Security & Privacy',
    tags: ['security', 'privacy', 'compliance'],
    difficulty: 'beginner',
  },
  {
    key: 'security-2',
    question: 'How do I manage user permissions and access?',
    answer: 'Administrators can manage user roles and permissions in Settings → Users & Permissions. Create custom roles with specific access levels. Assign users to roles and monitor their activity. Enable two-factor authentication for enhanced security.',
    category: 'Security & Privacy',
    tags: ['permissions', 'roles', 'access-control'],
    difficulty: 'advanced',
  },

  // Troubleshooting
  {
    key: 'troubleshooting-1',
    question: 'What should I do if I can\'t log in?',
    answer: 'First, check your internet connection and try refreshing the page. Use the "Forgot Password" link to reset your password. If two-factor authentication is enabled, ensure you have access to your backup codes. Contact support if issues persist.',
    category: 'Troubleshooting',
    tags: ['login', 'password', 'authentication'],
    difficulty: 'beginner',
  },
  {
    key: 'troubleshooting-2',
    question: 'How do I resolve upload and sync issues?',
    answer: 'Check your internet connection and file size limits. Clear your browser cache and cookies. Try uploading files individually if batch upload fails. Use the sync status indicator to monitor upload progress. Contact support for persistent issues.',
    category: 'Troubleshooting',
    tags: ['upload', 'sync', 'performance'],
    difficulty: 'intermediate',
  },
  {
    key: 'troubleshooting-3',
    question: 'How do I recover deleted data?',
    answer: 'Check the "Recently Deleted" section in the respective module. Deleted items are retained for 30 days. Use the restore function to recover items. For permanently deleted data, contact support immediately as recovery options may be available.',
    category: 'Troubleshooting',
    tags: ['recovery', 'deleted-data', 'backup'],
    difficulty: 'intermediate',
  },

  // Advanced Features
  {
    key: 'advanced-1',
    question: 'How do I use advanced analytics and reporting?',
    answer: 'Access advanced analytics in the Reports section. Create custom dashboards with key performance indicators. Use filters and date ranges for detailed analysis. Export data for external analysis and generate automated reports for regular business reviews.',
    category: 'Advanced Features',
    tags: ['analytics', 'reporting', 'dashboards'],
    difficulty: 'advanced',
    hasGuide: true,
  },
  {
    key: 'advanced-2',
    question: 'How do I set up automated workflows?',
    answer: 'Configure automated workflows in Settings → Workflows. Set up triggers for status changes, deadlines, and communications. Create automated notifications and task assignments. Use templates for consistent processes across all claims.',
    category: 'Advanced Features',
    tags: ['workflows', 'automation', 'templates'],
    difficulty: 'advanced',
  },
];

const FAQ: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);

  const categories = Array.from(new Set(faqData.map(item => item.category)));
  const allTags = Array.from(new Set(faqData.flatMap(item => item.tags)));
  const difficulties = ['beginner', 'intermediate', 'advanced'];

  const filteredFAQs = faqData.filter(item => {
    const matchesSearch = item.question.toLowerCase().includes(searchText.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchText.toLowerCase());
    const matchesCategory = !selectedCategory || item.category === selectedCategory;
    const matchesTags = selectedTags.length === 0 || selectedTags.some(tag => item.tags.includes(tag));
    const matchesDifficulty = !selectedDifficulty || item.difficulty === selectedDifficulty;
    return matchesSearch && matchesCategory && matchesTags && matchesDifficulty;
  });

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(selectedCategory === category ? null : category);
  };

  const handleTagClick = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleDifficultyClick = (difficulty: string) => {
    setSelectedDifficulty(selectedDifficulty === difficulty ? null : difficulty);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'green';
      case 'intermediate': return 'orange';
      case 'advanced': return 'red';
      default: return 'default';
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setSelectedCategory(null);
    setSelectedTags([]);
    setSelectedDifficulty(null);
  };

  return (
    <div className="p-6">
      <Card>
        <div className="flex justify-between items-center mb-6">
          <div>
            <Title level={3}>Frequently Asked Questions</Title>
            <Text type="secondary">Find answers to common questions and learn how to use the PA CRM system effectively</Text>
          </div>
          <Search
            placeholder="Search FAQs..."
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
            style={{ width: 350 }}
            size="large"
          />
        </div>

        {/* Filter Controls */}
        <div className="mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <Text strong>Filters:</Text>
            <Button size="small" onClick={clearFilters}>Clear All</Button>
          </div>
          
          <div>
            <Text strong className="mr-2">Categories:</Text>
            <Space wrap>
              {categories.map(category => (
                <Tag
                  key={category}
                  color={selectedCategory === category ? 'blue' : 'default'}
                  className="cursor-pointer hover:bg-blue-50"
                  onClick={() => handleCategoryClick(category)}
                >
                  {category}
                </Tag>
              ))}
            </Space>
          </div>

          <div>
            <Text strong className="mr-2">Difficulty:</Text>
            <Space wrap>
              {difficulties.map(difficulty => (
                <Tag
                  key={difficulty}
                  color={selectedDifficulty === difficulty ? getDifficultyColor(difficulty) : 'default'}
                  className="cursor-pointer capitalize"
                  onClick={() => handleDifficultyClick(difficulty)}
                >
                  {difficulty}
                </Tag>
              ))}
            </Space>
          </div>

          <div>
            <Text strong className="mr-2">Tags:</Text>
            <Space wrap>
              {allTags.map(tag => (
                <Tag
                  key={tag}
                  color={selectedTags.includes(tag) ? 'blue' : 'default'}
                  className="cursor-pointer hover:bg-blue-50"
                  onClick={() => handleTagClick(tag)}
                >
                  {tag}
                </Tag>
              ))}
            </Space>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4">
          <Text type="secondary">
            Showing {filteredFAQs.length} of {faqData.length} questions
          </Text>
        </div>

        {/* FAQ List */}
        <Collapse
          defaultActiveKey={['getting-started-1']}
          expandIconPosition="end"
          size="large"
        >
          {filteredFAQs.map(item => (
            <Panel
              key={item.key}
              header={
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <QuestionCircleOutlined className="mr-3 text-blue-500" />
                    <span className="font-medium">{item.question}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge 
                      count={item.difficulty} 
                      color={getDifficultyColor(item.difficulty)}
                      style={{ fontSize: '10px' }}
                    />
                    {item.hasVideo && (
                      <Tooltip title="Video tutorial available">
                        <VideoCameraOutlined className="text-green-500" />
                      </Tooltip>
                    )}
                    {item.hasGuide && (
                      <Tooltip title="Step-by-step guide available">
                        <BookOutlined className="text-blue-500" />
                      </Tooltip>
                    )}
                  </div>
                </div>
              }
            >
              <div className="pl-8">
                <Paragraph className="text-gray-700 leading-relaxed">
                  {item.answer}
                </Paragraph>
                <div className="mt-4 flex items-center justify-between">
                  <Space wrap>
                    <Tag color="blue">{item.category}</Tag>
                    {item.tags.map(tag => (
                      <Tag key={tag} size="small">{tag}</Tag>
                    ))}
                  </Space>
                  <div className="flex items-center space-x-2">
                    {item.hasVideo && (
                      <Button size="small" type="link" icon={<VideoCameraOutlined />}>
                        Watch Video
                      </Button>
                    )}
                    {item.hasGuide && (
                      <Button size="small" type="link" icon={<FileTextOutlined />}>
                        View Guide
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Panel>
          ))}
        </Collapse>

        {filteredFAQs.length === 0 && (
          <div className="text-center py-8">
            <QuestionCircleOutlined className="text-4xl text-gray-300 mb-4" />
            <Title level={4} type="secondary">No questions found</Title>
            <Text type="secondary">Try adjusting your search terms or filters</Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default FAQ; 